import { B64URL_RE } from "./constants.js";
import { LscError } from "./errors.js";

const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true, ignoreBOM: false });

export function utf8(s: string): Uint8Array {
  return encoder.encode(s);
}

export function utf8Decode(b: Uint8Array): string {
  try {
    return decoder.decode(b);
  } catch {
    throw new LscError("BAD_INPUT", "invalid UTF-8");
  }
}

/** base64url without padding (RFC 4648 §5). */
export function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  const STEP = 0x4000;
  for (let i = 0; i < bytes.length; i += STEP) {
    bin += String.fromCharCode(...bytes.subarray(i, i + STEP));
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function fromBase64Url(s: string): Uint8Array {
  if (typeof s !== "string" || !B64URL_RE.test(s)) {
    throw new LscError("BAD_INPUT", "invalid base64url alphabet");
  }
  if (s.length % 4 === 1) {
    throw new LscError("BAD_INPUT", "invalid base64url length");
  }
  const pad = (4 - (s.length % 4)) % 4;
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(pad);
  let bin: string;
  try {
    bin = atob(b64);
  } catch {
    throw new LscError("BAD_INPUT", "invalid base64url");
  }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function toHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += bytes[i]!.toString(16).padStart(2, "0");
  return s;
}

export function fromHex(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) {
    throw new LscError("BAD_INPUT", "invalid hex");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/** Constant-time equality for equal-length byte arrays (returns false on length mismatch). */
export function equalConstantTime(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

export function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", data));
}

/**
 * FIFO byte queue that assembles exact-size slices from arbitrarily sized input chunks
 * with a single copy per output byte. Used by both stream directions.
 */
export class ByteQueue {
  private parts: Uint8Array[] = [];
  private head = 0;
  length = 0;

  push(chunk: Uint8Array): void {
    if (chunk.length === 0) return;
    this.parts.push(chunk);
    this.length += chunk.length;
  }

  take(n: number): Uint8Array {
    if (n > this.length) throw new LscError("BAD_INPUT", "ByteQueue underflow");
    if (n === 0) return new Uint8Array(0);
    const first = this.parts[0]!;
    if (this.head === 0 && first.length === n) {
      this.parts.shift();
      this.length -= n;
      return first;
    }
    const out = new Uint8Array(n);
    let written = 0;
    while (written < n) {
      const p = this.parts[0]!;
      const avail = p.length - this.head;
      const k = Math.min(avail, n - written);
      out.set(p.subarray(this.head, this.head + k), written);
      written += k;
      this.head += k;
      if (this.head === p.length) {
        this.parts.shift();
        this.head = 0;
      }
    }
    this.length -= n;
    return out;
  }
}
